import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { TagSearchModal } from "../components/searchByTagModal/TagSearchModal";
import TableTab from "../components/tabs/table-tab";
import { ENROLMENTS } from "../constants/menu-types";
import { WS_URL } from "../constants/ws-constants";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import columns from "../models/ColumnDefinitions/EnrolmentColumns";
import { EnrolmentDevice } from "../models/EnrolmentDevices";
import { Filter } from "../models/Filter";
import { ListData } from "../models/ListData";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import { Name } from "../models/Name";
import CompanySearch from "../models/search/CompanySearch";
import DepartmentSearch from "../models/search/DepartmentSearch";
import searchColumns from "../models/search/EnrolmentSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getCompanyNamesWithPagination } from "../services/companyService";
import { getDepartmentNamesWithPagination } from "../services/departmentService";
import { deleteEnrolment, getEnrolments } from "../services/enrolmentService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { createProfileFilter } from "../services/utilities/customFilterBuilder";
import WSPluginService from "../services/wsPluginService/wsPluginService";
import {hideColumns} from "../models/utilities/columnHelper";
import { getCategoryNameWithPagination } from "../services/categoryService";
import CategorySearch from "../models/search/CategorySearch";

type Props = {
    category?: string;
    tableTitle?: string;
    id: number;
    resetCategories: () => void;
    addNewButton?: any;
    menuDetails?: string;
};

const EnrolmentsTab: FC<Props> = ({ category = "All", tableTitle = "All Enrollments", id = 0, resetCategories, addNewButton, menuDetails }) => {
    const itemPath = "/access-control/enrolment/personnel/";
    const [isOpen, setIsOpen] = useState(false);
    const [websocket, setWebsocket] = useState<WebSocket>();
    const { translate } = useLocale();
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [availablePlugins, setAvailablePlugins] = useState<Name[]>([]);
    const [openMethod, setOpenMethod] = useState<() => void | undefined>();
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        if (websocket && websocket.readyState == WebSocket.OPEN) {
            websocket?.send("{'enrollType':'any','validatePlugin':'true'}")
        }

        return (() => {
            websocket?.close();
        })
    }, [websocket, isOpen])

    useEffect(() => {
        availablePlugins.length > 0 ? setOpenMethod(() => openTagModalSearch) : setOpenMethod(undefined);
    }, [availablePlugins])

    const openTagModalSearch = () => {
        setIsOpen(true);
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        return new Promise((resolve, reject) => {
            deleteEnrolment(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        resetCategories();
                    },
                    deleteResult => {
                        reject(deleteResult);
                    })
        });
    }

    const findAvailablePlugin = (validatedPlugins) => {
        let plugins: Name[] = [];
        validatedPlugins.map((plugin) => {
            if (plugin && plugin.enrollType === enrolmentDevice.MDE.type && plugin.validatePlugin === "valid") {
                plugins.push({ label: "MDE", value: enrolmentDevice.MDE.type ?? '' })
            }
            else if (plugin && plugin.enrollType === enrolmentDevice.OMNIKEY.type && plugin.validatePlugin === "valid") {
                plugins.push({ label: "Omnikey", value: enrolmentDevice.OMNIKEY.type ?? '' })
            }
        });
        setAvailablePlugins(plugins);
    }

    const onMessage = (data) => {
        const response = JSON.parse(data);

        if (!!response && response.enrollType === 'any') {
            findAvailablePlugin(response.validatedPlugins)
        }
    }

    const onWebsocketClose = () => {
        websocket?.close();
    }

    const onError = (error) => {
        // Do not show a toast message for Plugin Manager not found. It is not a requirement.
        // We don't want to spam users who have no need for it.
        console.log('Plugin Manager not detected. Some enrolment capabilities will be unavailable.');
    }

    const onOpen = (webSocket: WebSocket, value) => {
        setWebsocket(webSocket)
    }

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const getUserPreferenceDetails = () => {
        // --> start context
        // user preference items
        const currentMenuDetail = menuDetails ?? ENROLMENTS;
        const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
            setDetails(userLoginId, currentMenuDetail);
        }

        return initialiseUserPreferences;
    }

    // todo - further refine whether tags types get added or removed in tag-related methods above.
    const [tagFilters, setTagFilters] = useState<Filter[]>(new Array(0));

    const getEnrolmentsForProfile = (id, query?: ListQuery): Promise<ListData> => {
        const profileFilter = createProfileFilter(id, tableTitle, false);
        const additionalFilters = profileFilter ? tagFilters.concat(profileFilter) : tagFilters;
        const newQuery = additionalFilters.length > 0
            ? (query
                ? {
                    ...query,
                    filters: [...query.filters ?? [], ...additionalFilters]
                }
                : {
                    filters: additionalFilters
                })
            : query;
        return getEnrolments(translate, newQuery);
    }

    // // default to a method that returns nothing
    // // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context
    return <>
        <WSPluginService
            onMessage={onMessage}
            onClosed={onWebsocketClose}
            onError={onError}
            onOpen={onOpen}
            url={window.location.protocol === "https:" ? WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET : WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET} />
        <TableContextProvider
            dataMethod={(listQuery) => getEnrolmentsForProfile(id, listQuery)}
            tableDescriptor={hideColumns(columns, ['email'])}
            searchDescriptor={searchColumns}
            useTable
            usePagination
            useFilter
            useSort
            useSearch
            initialiser={getUserPreferenceDetails()}
            manualDataRefresh={manualDataRefresh}
            deleteMethod={onDelete}
            lookup={lookup}>
            <TableTab
                addNewButton={addNewButton}
                title={tableTitle}
                selectRows={false}
                rowPath={itemPath}
                onTagSearchOpen={openMethod}
                cyName="enrolments">
                <TagSearchModal
                    availablePlugins={availablePlugins}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen} />
            </TableTab>
        </TableContextProvider>
    </>
};

export default EnrolmentsTab;
